'use client'

import { ReactNode } from 'react'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/next'

import AdInjector from 'components/AdInjector'
import CustomToaster from 'components/CustomToaster'
import Content from 'layout/Content'
import HydrationWarningCapture from 'components/HydrationWarningCapture'
import { DomSafetyPatch } from 'app/components/DomSafetyPatch'
import { ClientOnly } from 'app/components/client-only'
import { ConfigProvider } from 'components/providers/ConfigProvider'
import { SessionProvider as AppSessionProvider } from 'components/providers/SessionProvider'
import { TranslationProvider } from 'components/providers/TranslationProvider'

export interface AppProvidersProps {
    config: IDomainConfig
    lang: string
    user: IUser | null
    /** Whether the current user has admin access (GOD or assigned org/fed/comp admin). Used by nav to show Admin link. */
    hasAdminRole?: boolean
    children: ReactNode
}

/**
 * Composes config, session, and translation providers plus the global shell:
 * AdInjector, Content, toasts, SpeedInsights, Analytics.
 * Used by RootLayout so the provider stack and layout shell stay in one place.
 */
export function AppProviders({ config, lang, user, hasAdminRole, children }: AppProvidersProps) {
    return (
        <>
            <DomSafetyPatch />
            <ConfigProvider config={config}>
                <AppSessionProvider lang={lang} user={user} hasAdminRole={hasAdminRole}>
                    <TranslationProvider>
                        <HydrationWarningCapture />
                        <AdInjector />
                        <Content config={config}>{children}</Content>
                        {/* Defer Toaster until after mount to avoid hydration "removeChild" from Sonner's body portal */}
                        <ClientOnly>
                            <CustomToaster />
                        </ClientOnly>
                        <SpeedInsights />
                        <Analytics />
                    </TranslationProvider>
                </AppSessionProvider>
            </ConfigProvider>
        </>
    )
}
